import React, { useEffect, useRef } from 'react';

/* eslint-disable no-unused-vars */
type TurnstileProps = {
  siteKey: string;
  onTokenChange: (...args: [string | null]) => void;
  resetKey: number;
};

type TurnstileInstance = {
  render: (...args: [HTMLElement, Record<string, unknown>]) => string;
  reset: (...args: [string?]) => void;
  remove: (...args: [string]) => void;
};
/* eslint-enable no-unused-vars */

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let turnstileScriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  const turnstileWindow = window as Window & { turnstile?: TurnstileInstance };

  if (turnstileWindow.turnstile) {
    return Promise.resolve();
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      let retries = 0;
      const maxRetries = 100;

      const waitForTurnstile = () => {
        if (turnstileWindow.turnstile) {
          resolve();
          return;
        }

        if (retries >= maxRetries) {
          reject(new Error('Failed to initialize Turnstile'));
          return;
        }

        retries += 1;
        window.setTimeout(waitForTurnstile, 50);
      };

      waitForTurnstile();
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Turnstile script'));

    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
}

export const CloudflareTurnstile = ({ siteKey, onTokenChange, resetKey }: TurnstileProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    loadTurnstileScript()
      .then(() => {
        const turnstileWindow = window as Window & { turnstile?: TurnstileInstance };
        if (!mounted || !turnstileWindow.turnstile || !containerRef.current) return;

        if (widgetIdRef.current) {
          turnstileWindow.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }

        widgetIdRef.current = turnstileWindow.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => onTokenChange(token),
          'expired-callback': () => onTokenChange(null),
          'error-callback': () => onTokenChange(null),
        });
      })
      .catch(() => {
        onTokenChange(null);
      });

    return () => {
      mounted = false;
      const turnstileWindow = window as Window & { turnstile?: TurnstileInstance };

      if (turnstileWindow.turnstile && widgetIdRef.current) {
        turnstileWindow.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onTokenChange]);

  useEffect(() => {
    const turnstileWindow = window as Window & { turnstile?: TurnstileInstance };

    if (turnstileWindow.turnstile && widgetIdRef.current) {
      turnstileWindow.turnstile.reset(widgetIdRef.current);
      onTokenChange(null);
    }
  }, [resetKey, onTokenChange]);

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '10px 0',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div ref={containerRef} />
    </div>
  );
};

export default CloudflareTurnstile;
